import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useCash } from '../CashContext';
import { apiJson } from '../apiClient';
import { CashInOutPieChart, ChannelPopularityBars, ChannelVolumeBars } from '../components/CashInsightsCharts';
import {
  WAKALA_CHANNELS as channels,
  WAKALA_SERVICES_ALL as services,
  channelLabel,
  serviceFormLabel,
  serviceLabel,
  servicesForCashDirection,
  wakalaCashbookRows,
} from '../wakalaConstants';
import {
  aggregateByChannel,
  aggregateByService,
  dayTotals,
  lastNDaysSeries,
  summarizeTrendSeries,
  withNetFlow,
} from '../utils/cashAnalytics';
import { getLocalDateKey } from '../utils/reportDates';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from '../utils/submitFeedback';
import './CashPage.css';
import { normalizeRole } from '../utils/userLocale';

function formatSignedTz(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const v = Number(value);
  const lead = v > 0 ? '+' : '';
  return `${lead}${v.toLocaleString()}`;
}

/** Split API datetime (e.g. "2026-04-22 16:02:19") for a denser two-line cell. */
function splitTxDateTime(dateTime) {
  if (dateTime == null || typeof dateTime !== 'string') return { date: '—', time: '' };
  const trimmed = dateTime.trim();
  const space = trimmed.indexOf(' ');
  if (space === -1) return { date: trimmed, time: '' };
  return { date: trimmed.slice(0, space), time: trimmed.slice(space + 1).trim() };
}

function signedDeltaClass(value) {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  if (n > 0) return 'cash-delta-pos';
  if (n < 0) return 'cash-delta-neg';
  return 'cash-delta-zero';
}

function TxDateTimeCell({ value }) {
  const { date, time } = splitTxDateTime(value);
  return (
    <td className="cash-col-time" title={value || undefined}>
      <span className="cash-tx-time">
        <span className="cash-tx-time__date">{date}</span>
        {time ? <span className="cash-tx-time__clock">{time}</span> : null}
      </span>
    </td>
  );
}

function OwnerCashbookOpeningsCard({ channels, openings, onAfterSave }) {
  const floatChannels = useMemo(
    () => (channels || []).filter((c) => !['cash', 'card', 'other'].includes(c.value)),
    [channels]
  );
  const [physical, setPhysical] = useState('0');
  const [floatTotal, setFloatTotal] = useState('0');
  const [byLine, setByLine] = useState({});
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!openings) return;
    setPhysical(String(openings.physicalCashOpening ?? 0));
    setFloatTotal(String(openings.networkFloatOpening ?? 0));
    setByLine({ ...(openings.floatByChannel || {}) });
  }, [openings]);

  const setChannelVal = (key, raw) => {
    setByLine((prev) => ({ ...prev, [key]: raw }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const phys = Number(String(physical).replace(',', '.'));
    const flt = Number(String(floatTotal).replace(',', '.'));
    if (!Number.isFinite(phys) || phys < 0 || !Number.isFinite(flt) || flt < 0) {
      setMsg({ type: 'error', text: 'Enter valid numbers (≥ 0).' });
      return;
    }
    const floatByChannel = {};
    for (const c of floatChannels) {
      const raw = byLine[c.value];
      if (raw == null || raw === '') continue;
      const n = Number(String(raw).replace(',', '.'));
      if (Number.isFinite(n) && n > 0) floatByChannel[c.value] = Math.round(n * 100) / 100;
    }
    setBusy(true);
    try {
      await apiJson('/owner/cashbook-openings', {
        method: 'PATCH',
        body: JSON.stringify({
          physicalCashOpening: Math.round(phys * 100) / 100,
          networkFloatOpening: Math.round(flt * 100) / 100,
          floatByChannel,
        }),
      });
      setMsg({ type: 'success', text: 'Saved. Summary balances now include starting capital.' });
      if (typeof onAfterSave === 'function') await onAfterSave();
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cash-opening-card">
      <h4>Starting capital (owner)</h4>
      <p className="cash-opening-card__hint">
        Money already on hand before recorded cashbook activity: total float across mobile-money lines, physical drawer
        cash, and optional per-line float notes.
      </p>
      <form className="cash-opening-card__form" onSubmit={handleSubmit}>
        <div className="cash-opening-card__row2">
          <label className="cash-opening-card__field">
            Total network float (TZS)
            <input
              type="number"
              min="0"
              step="0.01"
              value={floatTotal}
              onChange={(e) => setFloatTotal(e.target.value)}
            />
          </label>
          <label className="cash-opening-card__field">
            Physical drawer cash (TZS)
            <input
              type="number"
              min="0"
              step="0.01"
              value={physical}
              onChange={(e) => setPhysical(e.target.value)}
            />
          </label>
        </div>
        <p className="cash-opening-card__subhead">Float by line (optional)</p>
        <div className="cash-opening-card__grid">
          {floatChannels.map((c) => (
            <label key={c.value} className="cash-opening-card__field">
              {c.label}
              <input
                type="number"
                min="0"
                step="0.01"
                value={byLine[c.value] ?? ''}
                placeholder="0"
                onChange={(e) => setChannelVal(c.value, e.target.value)}
              />
            </label>
          ))}
        </div>
        <button type="submit" className="cash-opening-card__submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save starting capital'}
        </button>
        {msg?.text ? (
          <p className={msg.type === 'success' ? 'cash-opening-card__msg cash-opening-card__msg--ok' : 'cash-opening-card__msg cash-opening-card__msg--err'}>
            {msg.text}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function movementByService(service, direction) {
  if (service === 'cash-drawer-out') {
    return { cash: -1, float: 0 };
  }
  if (service === 'float-topup') {
    return { cash: -1, float: 1 };
  }
  if (['mpesa-deposit', 'mpesa-lipa-in', 'airtime', 'billpay', 'banking'].includes(service)) {
    return { cash: 1, float: -1 };
  }
  if (['withdrawal', 'withdraw-agent', 'withdraw-lipa'].includes(service)) {
    return { cash: -1, float: 1 };
  }
  if (direction === 'in') {
    return { cash: 1, float: -1 };
  }
  return { cash: -1, float: 1 };
}

const CashPage = ({ mode = 'overview', onNavigate }) => {
  const { user: currentUser } = useAuth();
  const { floatBalance, cashBalance, transactions, stats, addTransaction, resetCashbook, refreshCashbook, openings } =
    useCash();
  const isOwner = normalizeRole(currentUser?.role) === 'owner';

  const go = useCallback(
    (key) => {
      if (typeof onNavigate === 'function') onNavigate(key);
    },
    [onNavigate]
  );

  const cashStatKeyDown = useCallback(
    (navKey) => (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        go(navKey);
      }
    },
    [go]
  );

  const [direction, setDirection] = useState('in');
  const [service, setService] = useState('mpesa-deposit');
  const [channel, setChannel] = useState('mpesa');
  const [amount, setAmount] = useState('');
  const [commissionTzs, setCommissionTzs] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState(null);
  const [savePhase, setSavePhase] = useState('idle');
  const redirectRef = useRef(null);

  const [filterDirection, setFilterDirection] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [search, setSearch] = useState('');
  const [insightDate, setInsightDate] = useState(() => getLocalDateKey());
  const [linePieDate, setLinePieDate] = useState(() => getLocalDateKey());
  const [linePieChannel, setLinePieChannel] = useState('mpesa');
  const [insightPieChannel, setInsightPieChannel] = useState('mpesa');

  const filteredHistory = useMemo(() => {
    return transactions.filter((t) => {
      const dirOk = filterDirection === 'all' || t.direction === filterDirection;
      const svcOk = filterService === 'all' || t.service === filterService;
      const hay = `${t.note} ${t.createdBy} ${t.channel} ${t.service} ${t.id}`.toLowerCase();
      const searchOk = !search || hay.includes(search.toLowerCase());
      return dirOk && svcOk && searchOk;
    });
  }, [transactions, filterDirection, filterService, search]);

  const wakalaTx = useMemo(() => wakalaCashbookRows(transactions), [transactions]);
  const trendSeries = useMemo(() => lastNDaysSeries(wakalaTx, 7), [wakalaTx]);

  const channelDay = useMemo(() => aggregateByChannel(wakalaTx, insightDate), [wakalaTx, insightDate]);
  const serviceDay = useMemo(() => aggregateByService(wakalaTx, insightDate), [wakalaTx, insightDate]);
  const dayRollup = useMemo(() => dayTotals(wakalaTx, insightDate), [wakalaTx, insightDate]);
  const channelPopularity = useMemo(() => [...channelDay].sort((a, b) => b.txnCount - a.txnCount), [channelDay]);
  const channelMaxVol = useMemo(
    () => Math.max(0, ...channelDay.map((r) => r.cashIn + r.cashOut)),
    [channelDay]
  );
  const serviceMaxVol = useMemo(
    () => Math.max(0, ...serviceDay.map((r) => r.cashIn + r.cashOut)),
    [serviceDay]
  );

  const trendSummary = useMemo(() => summarizeTrendSeries(trendSeries), [trendSeries]);
  const todayByNetwork = useMemo(() => {
    const tk = getLocalDateKey();
    return withNetFlow(aggregateByChannel(wakalaTx, tk)).sort(
      (a, b) => b.cashIn + b.cashOut - (a.cashIn + a.cashOut)
    );
  }, [wakalaTx]);
  const insightByNetwork = useMemo(
    () => withNetFlow(aggregateByChannel(wakalaTx, insightDate)).sort((a, b) => b.net - a.net),
    [wakalaTx, insightDate]
  );

  const overviewLineBucket = useMemo(() => {
    const rows = aggregateByChannel(wakalaTx, linePieDate);
    return (
      rows.find((r) => r.key === linePieChannel) || {
        key: linePieChannel,
        cashIn: 0,
        cashOut: 0,
        txnCount: 0,
      }
    );
  }, [wakalaTx, linePieDate, linePieChannel]);

  const insightLineBucket = useMemo(() => {
    const rows = aggregateByChannel(wakalaTx, insightDate);
    return (
      rows.find((r) => r.key === insightPieChannel) || {
        key: insightPieChannel,
        cashIn: 0,
        cashOut: 0,
        txnCount: 0,
      }
    );
  }, [wakalaTx, insightDate, insightPieChannel]);

  useEffect(() => {
    return () => {
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof refreshCashbook === 'function') refreshCashbook();
  }, [mode, refreshCashbook]);

  useEffect(() => {
    if (!isOwner && mode === 'insights' && typeof onNavigate === 'function') {
      onNavigate('cash');
    }
  }, [isOwner, mode, onNavigate]);

  const servicesForNewTx = useMemo(() => servicesForCashDirection(direction), [direction]);
  const customerServicesForNewTx = useMemo(
    () => servicesForNewTx.filter((s) => s.value !== 'float-topup'),
    [servicesForNewTx]
  );
  const capitalServicesForNewTx = useMemo(
    () => servicesForNewTx.filter((s) => s.value === 'float-topup'),
    [servicesForNewTx]
  );

  useEffect(() => {
    setService((prev) =>
      servicesForNewTx.some((s) => s.value === prev) ? prev : servicesForNewTx[0]?.value || prev
    );
  }, [servicesForNewTx]);

  const saveBusy = savePhase === 'loading' || savePhase === 'success';
  const flow = useMemo(() => movementByService(service, direction), [service, direction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savePhase !== 'idle') return;
    if (redirectRef.current) {
      clearTimeout(redirectRef.current);
      redirectRef.current = null;
    }
    setMessage(null);
    setSavePhase('loading');
    const started = Date.now();
    try {
      await addTransaction({
        direction,
        service,
        channel,
        amount,
        commission: commissionTzs,
        commissionPercent: undefined,
        note,
      });
      await waitUntilMinElapsed(started);
      setMessage({ type: 'success', text: 'Transaction saved.' });
      setAmount('');
      setNote('');
      setCommissionTzs('');
      setSavePhase('success');
      redirectRef.current = setTimeout(() => {
        redirectRef.current = null;
        setSavePhase('idle');
        setMessage(null);
        if (typeof onNavigate === 'function') onNavigate('cash-history');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to save transaction.' });
      setSavePhase('idle');
    }
  };

  return (
    <div className="cash-root page-bleed">
      <header className="cash-header">
        <h1 className="cash-title">Wakala cashbook</h1>
        <p className="cash-header-sub">
          {isOwner
            ? 'Telco float, physical drawer, and cash-in / cash-out — record transactions, review history, and see daily insights.'
            : 'Telco float, physical drawer, and cash-in / cash-out — record transactions and review history.'}
        </p>
      </header>

      {typeof onNavigate === 'function' && (
        <div className="cash-subnav" role="tablist" aria-label="Wakala cashbook">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'overview'}
            className={mode === 'overview' ? 'is-active' : ''}
            onClick={() => onNavigate('cash')}
          >
            Summary
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'new'}
            className={mode === 'new' ? 'is-active' : ''}
            onClick={() => onNavigate('cash-new')}
          >
            New transaction
          </button>
          {isOwner ? (
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'insights'}
            className={mode === 'insights' ? 'is-active' : ''}
            onClick={() => onNavigate('cash-insights')}
          >
            Daily insights
          </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'history'}
            className={mode === 'history' ? 'is-active' : ''}
            onClick={() => onNavigate('cash-history')}
          >
            TX history
          </button>
        </div>
      )}

      <div className="cash-grid">
        <div
          className="cash-card cash-card--clickable cash-card--tone-float"
          role="link"
          tabIndex={0}
          title="Open TX history"
          onClick={() => go('cash-history')}
          onKeyDown={cashStatKeyDown('cash-history')}
        >
          <span>Float</span>
          <strong>TZS {floatBalance.toLocaleString()}</strong>
        </div>
        <div
          className="cash-card cash-card--clickable cash-card--tone-drawer"
          role="link"
          tabIndex={0}
          title="Open TX history"
          onClick={() => go('cash-history')}
          onKeyDown={cashStatKeyDown('cash-history')}
        >
          <span>Dro</span>
          <strong>{cashBalance == null ? '—' : `TZS ${cashBalance.toLocaleString()}`}</strong>
        </div>
        <div
          className="cash-card cash-card--clickable cash-card--tone-in"
          role="link"
          tabIndex={0}
          title={isOwner ? 'Open daily insights' : 'Open TX history'}
          onClick={() => go(isOwner ? 'cash-insights' : 'cash-history')}
          onKeyDown={cashStatKeyDown(isOwner ? 'cash-insights' : 'cash-history')}
        >
          <span>Cash in · today</span>
          <strong>TZS {stats.cashIn.toLocaleString()}</strong>
        </div>
        <div
          className="cash-card cash-card--clickable cash-card--tone-out"
          role="link"
          tabIndex={0}
          title={isOwner ? 'Open daily insights' : 'Open TX history'}
          onClick={() => go(isOwner ? 'cash-insights' : 'cash-history')}
          onKeyDown={cashStatKeyDown(isOwner ? 'cash-insights' : 'cash-history')}
        >
          <span>Cash out · today</span>
          <strong>TZS {stats.cashOut.toLocaleString()}</strong>
        </div>
        {isOwner ? (
        <div
          className="cash-card cash-card--clickable cash-card--tone-comm"
          role="link"
          tabIndex={0}
          title="Open TX history"
          onClick={() => go('cash-history')}
          onKeyDown={cashStatKeyDown('cash-history')}
        >
          <span>Commission · today</span>
          <strong>TZS {stats.commission.toLocaleString()}</strong>
        </div>
        ) : null}
        <div
          className="cash-card cash-card--clickable cash-card--tone-count"
          role="link"
          tabIndex={0}
          title="Open TX history"
          onClick={() => go('cash-history')}
          onKeyDown={cashStatKeyDown('cash-history')}
        >
          <span>Tx · today</span>
          <strong>{stats.todayCount}</strong>
        </div>
      </div>

      {mode === 'overview' && (
        <div className="cash-panel">
          <h3>Summary</h3>

          <div className="cash-today-lines">
            <h4>Today · by network</h4>
            <div className="cash-table-wrap cash-table-wrap--ledger">
              <table className="cash-table cash-table--compact cash-table--wakala-summary">
                <thead>
                  <tr>
                    <th>Network</th>
                    <th>Cash-in (TZS)</th>
                    <th>Cash-out (TZS)</th>
                    <th>Net (TZS)</th>
                    <th>Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {todayByNetwork.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="cash-table-empty">
                        No transactions yet today.
                      </td>
                    </tr>
                  ) : (
                    todayByNetwork.map((r) => (
                      <tr key={r.key}>
                        <td>{channelLabel(r.key)}</td>
                        <td className="cash-cell-in">{r.cashIn ? Number(r.cashIn).toLocaleString() : '—'}</td>
                        <td className="cash-cell-out">{r.cashOut ? Number(r.cashOut).toLocaleString() : '—'}</td>
                        <td className={r.net >= 0 ? 'cash-cell-net-pos' : 'cash-cell-net-neg'}>
                          {r.net >= 0 ? '+' : ''}
                          {Number(r.net).toLocaleString()}
                        </td>
                        <td>{r.txnCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <h4 className="cash-recent-title">Last 8 lines</h4>
          <div className="cash-table-wrap cash-table-wrap--scroll cash-table-wrap--ledger">
            <table className="cash-table cash-table--tx cash-table--tx-ledger">
              <thead>
                <tr>
                  <th scope="col" className="cash-col-time">
                    <abbr title="Date and time">Time</abbr>
                  </th>
                  <th scope="col" className="cash-col-dir">
                    <abbr title="Cash direction">Dir</abbr>
                  </th>
                  <th scope="col" className="cash-col-service">
                    <abbr title="Service type">Svc</abbr>
                  </th>
                  <th scope="col" className="cash-col-network">
                    <abbr title="Network / line">Line</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Cash-in (TZS)">In</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Cash-out (TZS)">Out</abbr>
                  </th>
                  {isOwner ? (
                  <th scope="col" className="cash-col-comm">
                    <abbr title="Commission (TZS)">Comm</abbr>
                  </th>
                  ) : null}
                  <th scope="col" className="cash-col-num">
                    <abbr title="Physical drawer change">Dro Δ</abbr>
                  </th>
                  <th scope="col" className="cash-col-num cash-col-num--wide">
                    <abbr title="Paired e-money float change">Fl.Δ</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Drawer balance after">Dro</abbr>
                  </th>
                  <th scope="col" className="cash-col-num cash-col-num--wide">
                    <abbr title="E-money float on line after this row">Fl. after</abbr>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 8).length === 0 ? (
                  <tr>
                    <td colSpan={isOwner ? 11 : 10} className="cash-table-empty">
                      No transactions yet.
                    </td>
                  </tr>
                ) : (
                  transactions.slice(0, 8).map((t) => (
                    <tr key={t.id}>
                      <TxDateTimeCell value={t.dateTime} />
                      <td className="cash-col-dir" title={t.direction === 'in' ? 'Cash-in' : 'Cash-out'}>
                        <span className={`cash-dir-badge cash-dir-badge--${t.direction}`}>
                          {t.direction === 'in' ? 'In' : 'Out'}
                        </span>
                      </td>
                      <td className="cash-col-service" title={serviceLabel(t.service)}>
                        {serviceFormLabel(t.service)}
                      </td>
                      <td className="cash-col-network" title={channelLabel(t.channel)}>
                        {channelLabel(t.channel)}
                      </td>
                      <td className="cash-col-num cash-cell-in">
                        {t.direction === 'in' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td className="cash-col-num cash-cell-out">
                        {t.direction === 'out' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      {isOwner ? <td className="cash-col-comm">{Number(t.commission).toLocaleString()}</td> : null}
                      <td className={`cash-col-num ${signedDeltaClass(t.physicalCashChange)}`}>
                        {formatSignedTz(t.physicalCashChange)}
                      </td>
                      <td className={`cash-col-num cash-col-num--wide ${signedDeltaClass(t.networkFloatPairChange)}`}>
                        {formatSignedTz(t.networkFloatPairChange)}
                      </td>
                      <td className="cash-col-num">
                        {t.physicalCashAfter != null ? Number(t.physicalCashAfter).toLocaleString() : '—'}
                      </td>
                      <td className="cash-col-num cash-col-num--wide">{Number(t.floatAfter).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="cash-overview-trend">
            <h4>In vs out · one network</h4>
            <div className="cash-pie-controls">
              <label htmlFor="cash-line-pie-date">
                Date
                <input
                  id="cash-line-pie-date"
                  type="date"
                  value={linePieDate}
                  max={getLocalDateKey()}
                  onChange={(e) => setLinePieDate(e.target.value || getLocalDateKey())}
                />
              </label>
              <label htmlFor="cash-line-pie-channel">
                Network
                <select
                  id="cash-line-pie-channel"
                  value={linePieChannel}
                  onChange={(e) => setLinePieChannel(e.target.value)}
                >
                  {channels.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <CashInOutPieChart
              cashIn={overviewLineBucket.cashIn}
              cashOut={overviewLineBucket.cashOut}
              lineLabel={channelLabel(overviewLineBucket.key)}
              dateLabel={linePieDate}
              txnCount={overviewLineBucket.txnCount}
            />
            {trendSummary.days > 0 ? (
              <div className="cash-trend-compact" role="status">
                <span className="cash-trend-compact-label">7 days</span>
                <span>
                  In TZS {trendSummary.totalIn.toLocaleString()} · Out {trendSummary.totalOut.toLocaleString()} ·{' '}
                  <span className={trendSummary.net >= 0 ? 'cash-cell-net-pos' : 'cash-cell-net-neg'}>
                    Net {trendSummary.net >= 0 ? '+' : ''}
                    {trendSummary.net.toLocaleString()}
                  </span>
                </span>
                <span className="cash-trend-compact-meta">
                  Busiest day: {trendSummary.busiestDay?.dateKey} ({trendSummary.busiestDay?.count || 0} tx)
                </span>
              </div>
            ) : null}
          </div>
          {isOwner ? (
            <OwnerCashbookOpeningsCard channels={channels} openings={openings} onAfterSave={refreshCashbook} />
          ) : null}
        </div>
      )}

      {mode === 'insights' && isOwner && (
        <div className="cash-panel cash-panel--insights">
          <h3>Daily insights</h3>
          <div className="cash-insights-head">
            <label htmlFor="cash-insight-date">
              Date
              <input
                id="cash-insight-date"
                type="date"
                value={insightDate}
                max={getLocalDateKey()}
                onChange={(e) => setInsightDate(e.target.value || getLocalDateKey())}
              />
            </label>
          </div>
          <div className="cash-insights-summary">
            <div
              className="cash-card cash-card--clickable cash-card--tone-in"
              role="link"
              tabIndex={0}
              title="Open TX history"
              onClick={() => go('cash-history')}
              onKeyDown={cashStatKeyDown('cash-history')}
            >
              <span>Cash in</span>
              <strong>TZS {dayRollup.cashIn.toLocaleString()}</strong>
            </div>
            <div
              className="cash-card cash-card--clickable cash-card--tone-out"
              role="link"
              tabIndex={0}
              title="Open TX history"
              onClick={() => go('cash-history')}
              onKeyDown={cashStatKeyDown('cash-history')}
            >
              <span>Cash out</span>
              <strong>TZS {dayRollup.cashOut.toLocaleString()}</strong>
            </div>
            <div
              className="cash-card cash-card--clickable cash-card--tone-float"
              role="link"
              tabIndex={0}
              title="Open TX history"
              onClick={() => go('cash-history')}
              onKeyDown={cashStatKeyDown('cash-history')}
            >
              <span>Net</span>
              <strong>TZS {dayRollup.netInMinusOut.toLocaleString()}</strong>
            </div>
            <div
              className="cash-card cash-card--clickable cash-card--tone-count"
              role="link"
              tabIndex={0}
              title="Open TX history"
              onClick={() => go('cash-history')}
              onKeyDown={cashStatKeyDown('cash-history')}
            >
              <span>Tx count</span>
              <strong>{dayRollup.count}</strong>
            </div>
          </div>

          <div className="cash-insight-panel cash-insight-panel--full">
            <h4>Net by network · {insightDate}</h4>
            <div className="cash-table-wrap cash-table-wrap--ledger">
              <table className="cash-table cash-table--compact cash-table--wakala-summary">
                <thead>
                  <tr>
                    <th>Network</th>
                    <th>Cash-in</th>
                    <th>Cash-out</th>
                    <th>Net</th>
                    <th>Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {insightByNetwork.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="cash-table-empty">
                        No transactions on this date.
                      </td>
                    </tr>
                  ) : (
                    insightByNetwork.map((r) => (
                      <tr key={r.key}>
                        <td>{channelLabel(r.key)}</td>
                        <td className="cash-cell-in">{r.cashIn ? Number(r.cashIn).toLocaleString() : '—'}</td>
                        <td className="cash-cell-out">{r.cashOut ? Number(r.cashOut).toLocaleString() : '—'}</td>
                        <td className={r.net >= 0 ? 'cash-cell-net-pos' : 'cash-cell-net-neg'}>
                          {r.net >= 0 ? '+' : ''}
                          {Number(r.net).toLocaleString()}
                        </td>
                        <td>{r.txnCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cash-insights-grid">
            <div className="cash-insight-panel cash-insight-panel--mint">
              <h4>Network · amounts</h4>
              <ChannelVolumeBars rows={channelDay} maxVolume={channelMaxVol} labelFn={channelLabel} />
            </div>
            <div className="cash-insight-panel cash-insight-panel--sand">
              <h4>Network · activity</h4>
              <ChannelPopularityBars rows={channelPopularity} labelFn={channelLabel} />
            </div>
            <div className="cash-insight-panel cash-insight-panel--lav">
              <h4>Service · amounts</h4>
              <ChannelVolumeBars rows={serviceDay} maxVolume={serviceMaxVol} labelFn={serviceLabel} />
            </div>
            <div className="cash-insight-panel cash-insight-panel--sky">
              <h4>In vs out · one line</h4>
              <div className="cash-pie-controls">
                <label htmlFor="cash-insight-pie-channel">
                  Network
                  <select
                    id="cash-insight-pie-channel"
                    value={insightPieChannel}
                    onChange={(e) => setInsightPieChannel(e.target.value)}
                  >
                    {channels.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <CashInOutPieChart
                cashIn={insightLineBucket.cashIn}
                cashOut={insightLineBucket.cashOut}
                lineLabel={channelLabel(insightLineBucket.key)}
                dateLabel={insightDate}
                txnCount={insightLineBucket.txnCount}
              />
            </div>
          </div>
        </div>
      )}

      {mode === 'new' && (
        <div className="cash-panel cash-panel--form">
          <h3>New transaction</h3>
          <form className="cash-form cash-form--new-tx" onSubmit={handleSubmit}>
            <div className="cash-field cash-field--span2">
              <span className="cash-field-label">Direction</span>
              <div className="cash-dir-toggle" role="group" aria-label="Cash direction">
                <button
                  type="button"
                  className={direction === 'in' ? 'is-active' : ''}
                  disabled={saveBusy}
                  onClick={() => setDirection('in')}
                >
                  Cash in
                </button>
                <button
                  type="button"
                  className={direction === 'out' ? 'is-active' : ''}
                  disabled={saveBusy}
                  onClick={() => setDirection('out')}
                >
                  Cash out
                </button>
              </div>
              <div className="cash-logic-note" role="note" aria-live="polite">
                <strong>{direction === 'in' ? 'Cash in' : 'Cash out'}:</strong>{' '}
                {direction === 'in'
                  ? 'mteja anakupa cash, unamtumia float. '
                  : 'mteja anakupa float, unampa cash. '}
                huduma ya <strong>{serviceFormLabel(service)}</strong> itasababisha{' '}
                {flow.cash > 0 ? (
                  <span className="cash-logic-plus">Cash (Dro) +</span>
                ) : flow.cash < 0 ? (
                  <span className="cash-logic-minus">Cash (Dro) -</span>
                ) : (
                  <span>Cash (Dro) 0</span>
                )}{' '}
                na{' '}
                {flow.float > 0 ? (
                  <span className="cash-logic-plus">Float +</span>
                ) : flow.float < 0 ? (
                  <span className="cash-logic-minus">Float -</span>
                ) : (
                  <span>Float 0</span>
                )}
                .
              </div>
            </div>
            <div className="cash-field">
              <label htmlFor="cash-new-service">Service</label>
              <select
                id="cash-new-service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                disabled={saveBusy}
              >
                {direction === 'in' ? (
                  <>
                    <optgroup label="Customer services">
                      {customerServicesForNewTx.map((s) => (
                        <option key={s.value} value={s.value}>
                          {serviceFormLabel(s.value)}
                        </option>
                      ))}
                    </optgroup>
                    {capitalServicesForNewTx.length > 0 ? (
                      <optgroup label="Capital action">
                        {capitalServicesForNewTx.map((s) => (
                          <option key={s.value} value={s.value}>
                            {serviceFormLabel(s.value)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </>
                ) : (
                  servicesForNewTx.map((s) => (
                    <option key={s.value} value={s.value}>
                      {serviceFormLabel(s.value)}
                    </option>
                  ))
                )}
              </select>
              {direction === 'in' ? (
                <p className="cash-field-help">Tip: Float top-up is a capital action, not a normal customer service.</p>
              ) : null}
            </div>
            <div className="cash-field">
              <label htmlFor="cash-new-channel">Network</label>
              <select
                id="cash-new-channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={saveBusy}
              >
                {channels.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="cash-field">
              <label htmlFor="cash-new-amount">Amount (TZS)</label>
              <input
                id="cash-new-amount"
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                disabled={saveBusy}
              />
            </div>
            <div className="cash-field">
              <label htmlFor="cash-new-commission">Commission (TZS)</label>
              <input
                id="cash-new-commission"
                type="number"
                min="0"
                step="1"
                value={commissionTzs}
                onChange={(e) => setCommissionTzs(e.target.value)}
                disabled={saveBusy}
                placeholder="0"
              />
              <p className="cash-field-help">
                Optional. {isOwner ? 'Visible in owner reports.' : 'Saved for owner reporting; hidden on staff financial views.'}
              </p>
            </div>
            <div className="cash-field" style={{ gridColumn: '1 / -1' }}>
              <label>Note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ref, name…"
                disabled={saveBusy}
              />
            </div>
            <div className="cash-actions">
              <button className="cash-btn-primary" type="submit" disabled={saveBusy}>
                {savePhase === 'loading' ? (
                  <>
                    <span className="submit-spinner" aria-hidden />
                    Saving…
                  </>
                ) : savePhase === 'success' ? (
                  'Redirecting…'
                ) : (
                  'Save'
                )}
              </button>
              <button
                className="cash-btn-secondary"
                type="button"
                onClick={async () => {
                  if (!isOwner) return;
                  setMessage(null);
                  try {
                    await resetCashbook();
                    setMessage({ type: 'success', text: 'Cashbook cleared.' });
                  } catch (err) {
                    setMessage({ type: 'error', text: err?.message || 'Could not reset cashbook.' });
                  }
                }}
                disabled={saveBusy || !isOwner}
                title={!isOwner ? 'Owner only.' : undefined}
              >
                Reset
              </button>
            </div>
            {message?.type === 'success' && <div className="form-message-global success">{message.text}</div>}
            {message?.type === 'error' && <div className="form-message-global error">{message.text}</div>}
          </form>
        </div>
      )}

      {mode === 'history' && (
        <div className="cash-panel cash-panel--history">
          <div className="cash-history-head">
            <div className="cash-history-head__titles">
              <h3>TX history</h3>
              <p className="cash-history-head__meta" aria-live="polite">
                Showing <strong>{filteredHistory.length}</strong>
                {transactions.length !== filteredHistory.length ? (
                  <>
                    {' '}
                    of <strong>{transactions.length}</strong>
                  </>
                ) : null}{' '}
                · scroll sideways for drawer and float columns
              </p>
            </div>
          </div>
          <div className="cash-history-toolbar" role="search">
            <div className="cash-history-toolbar__filters">
              <label className="cash-history-field">
                <span className="cash-history-field__label">Direction</span>
                <select value={filterDirection} onChange={(e) => setFilterDirection(e.target.value)}>
                  <option value="all">All</option>
                  <option value="in">Cash-in</option>
                  <option value="out">Cash-out</option>
                </select>
              </label>
              <label className="cash-history-field">
                <span className="cash-history-field__label">Service</span>
                <select value={filterService} onChange={(e) => setFilterService(e.target.value)}>
                  <option value="all">All</option>
                  {services.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="cash-history-field cash-history-field--grow">
              <span className="cash-history-field__label">Search</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Note, user, line, id…"
                autoComplete="off"
              />
            </label>
          </div>
          <div className="cash-table-wrap cash-table-wrap--scroll cash-table-wrap--ledger">
            <table className="cash-table cash-table--tx cash-table--tx-wide cash-table--tx-ledger">
              <thead>
                <tr>
                  <th scope="col" className="cash-col-time">
                    <abbr title="Date and time">Time</abbr>
                  </th>
                  <th scope="col" className="cash-col-dir">
                    <abbr title="Cash direction">Dir</abbr>
                  </th>
                  <th scope="col" className="cash-col-service">
                    <abbr title="Service type">Svc</abbr>
                  </th>
                  <th scope="col" className="cash-col-network">
                    <abbr title="Network / line">Line</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Cash-in (TZS)">In</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Cash-out (TZS)">Out</abbr>
                  </th>
                  {isOwner ? (
                  <th scope="col" className="cash-col-comm">
                    <abbr title="Commission (TZS)">Comm</abbr>
                  </th>
                  ) : null}
                  <th scope="col" className="cash-col-by">
                    <abbr title="Recorded by">By</abbr>
                  </th>
                  <th scope="col" className="cash-col-note">
                    <abbr title="Note">Note</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Physical drawer change">Dro Δ</abbr>
                  </th>
                  <th scope="col" className="cash-col-num cash-col-num--wide">
                    <abbr title="Paired e-money float change">Fl.Δ</abbr>
                  </th>
                  <th scope="col" className="cash-col-num">
                    <abbr title="Drawer balance after">Dro</abbr>
                  </th>
                  <th scope="col" className="cash-col-num cash-col-num--wide">
                    <abbr title="E-money float on line after this row">Fl. after</abbr>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={isOwner ? 13 : 12} className="cash-table-empty">
                      No rows match filters.
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((t) => (
                    <tr key={t.id}>
                      <TxDateTimeCell value={t.dateTime} />
                      <td className="cash-col-dir" title={t.direction === 'in' ? 'Cash-in' : 'Cash-out'}>
                        <span className={`cash-dir-badge cash-dir-badge--${t.direction}`}>
                          {t.direction === 'in' ? 'In' : 'Out'}
                        </span>
                      </td>
                      <td className="cash-col-service" title={serviceLabel(t.service)}>
                        {serviceFormLabel(t.service)}
                      </td>
                      <td className="cash-col-network" title={channelLabel(t.channel)}>
                        {channelLabel(t.channel)}
                      </td>
                      <td className="cash-col-num cash-cell-in">
                        {t.direction === 'in' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td className="cash-col-num cash-cell-out">
                        {t.direction === 'out' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      {isOwner ? <td className="cash-col-comm">{Number(t.commission).toLocaleString()}</td> : null}
                      <td className="cash-col-by" title={t.createdBy || ''}>
                        {t.createdBy || '—'}
                      </td>
                      <td className="cash-col-note" title={t.note || ''}>
                        {t.note || '—'}
                      </td>
                      <td className={`cash-col-num ${signedDeltaClass(t.physicalCashChange)}`}>
                        {formatSignedTz(t.physicalCashChange)}
                      </td>
                      <td className={`cash-col-num cash-col-num--wide ${signedDeltaClass(t.networkFloatPairChange)}`}>
                        {formatSignedTz(t.networkFloatPairChange)}
                      </td>
                      <td className="cash-col-num">
                        {t.physicalCashAfter != null ? Number(t.physicalCashAfter).toLocaleString() : '—'}
                      </td>
                      <td className="cash-col-num cash-col-num--wide">{Number(t.floatAfter).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashPage;
